import {
  AliasType,
  GetContextSearchQuery,
  GetContextSearchQueryVariables,
} from '../gql/graphql';
import { QueryHookOptions, useLazyQuery } from '@apollo/client';

import { FirstPaginationNode } from './types';
import { ISearchResultGroup } from './search.types';
import { asCase } from '@converge/utils';
import { convergeLogger } from '@converge/config';
import { getPaginatedNodes } from './utils';
import { graphql } from '../gql/gql';
import pluralize from 'pluralize';
import { useMemo } from 'react';

/**
 * Unified V3 search hook.
 *
 * Calls searchV3 with whatever context the caller supplies in
 * filters.context ("submissions", "claims", etc.).  The backend routes to
 * the appropriate domain search class, returns real Neo4j scores, and
 * populates detectedInputType so the UI can reflect what kind of search
 * was performed.
 *
 * Adding a new context:
 *   1. Create a domain search class on the backend (see CLAUDE.md)
 *   2. Add the context string to CONTEXT_REGISTRY in search_v3.py
 *   3. Call useSearch() with filters.context set to the new context string
 */
const CONTEXT_SEARCH_QUERY = graphql(`
  query getContextSearch(
    $filters: SearchV2FilterInput
    $pagination: PaginationRequest
    $sort: PaginationSort
  ) {
    searchV3(filters: $filters, pagination: $pagination, sort: $sort) {
      pageInfo {
        totalResults
        pageNumber
        pageSize
        hasNextPage
        hasPreviousPage
      }
      edges {
        cursor
        node {
          group
          uuid
          score
          detectedInputType
          data {
            ... on SearchSubmission {
              uuid
              referenceId
              submissionStatus: status
              matchedOn
              matchedSource
              matchedType
              entities {
                uuid
                type
                matchedOn
                aliases {
                  uuid
                  name
                  source
                  type
                  matchedOn
                }
              }
            }
            ... on SearchClaim {
              uuid
              referenceId
              claimStatus: status
              entity {
                uuid
                referenceId
                aliases {
                  uuid
                  name
                  type
                }
              }
              persons {
                uuid
                firstName
                lastName
              }
            }
            ... on SearchEntity {
              uuid
              referenceId
              type
              entityStatus: status
              matchedOn
              aliases {
                uuid
                name
                type
              }
            }
            ... on SearchPerson {
              uuid
              firstName
              lastName
              personStatus: status
              matchedOn
            }
          }
        }
      }
    }
  }
`);

export type ContextSearchResultDetails = FirstPaginationNode<
  GetContextSearchQuery['searchV3']
>;

export const useSearch = (
  options: QueryHookOptions<
    GetContextSearchQuery,
    GetContextSearchQueryVariables
  > = {}
) => {
  const [search, result] = useLazyQuery(CONTEXT_SEARCH_QUERY, options);

  const {
    searchResults,
    searchResultsGrouped,
    remainingResults,
    remainingResultsText,
    totalResultsLoaded,
    pageSize,
  } = useMemo(() => {
    const searchResults = getPaginatedNodes(result.data?.searchV3).map(
      (node) => {
        let name: string = node.data?.__typename ?? '';
        let description = '';
        let status: string | null = null;
        const referenceId =
          node.data && 'referenceId' in node.data
            ? node.data.referenceId
            : undefined;

        switch (node.data?.__typename) {
          case 'SearchSubmission': {
            name = getSearchSubmissionName(node);
            description = referenceId ?? '';
            status =
              (node.data as { submissionStatus?: string | null })
                .submissionStatus ?? null;
            break;
          }
          case 'SearchClaim':
            name = getSearchClaimName(node);
            description = referenceId ?? '';
            status =
              (node.data as { claimStatus?: string | null }).claimStatus ??
              null;
            break;
          case 'SearchEntity': {
            name = getSearchEntityName(node);
            description = referenceId ?? '';
            const entityStatus = (node.data as { entityStatus?: string | null })
              .entityStatus;
            status =
              entityStatus === 'OUT_OF_BUSINESS'
                ? 'INACTIVE'
                : entityStatus ?? null;
            break;
          }
          case 'SearchPerson': {
            name = getSearchPersonName(node);
            description = '';
            const personStatus = (node.data as { personStatus?: string | null })
              .personStatus;
            status = personStatus ? personStatus.toUpperCase() : null;
            break;
          }
          default:
            name = node.data?.__typename?.startsWith('Search')
              ? node.data.__typename.replace('Search', '')
              : 'Other';
        }

        return {
          ...node,
          name,
          description,
          referenceId,
          status,
        };
      }
    );

    const searchResultsGroupedRecord = searchResults.reduce((acc, curr) => {
      const group = curr.group ?? 'OTHER';
      if (!acc[group]) {
        const displayName = asCase(group, asCase.StringCase.Sentence);
        acc[group] = {
          id: group,
          displayNamePlural: pluralize(displayName),
          displayName,
          results: [],
          averageScore: curr.score ?? 0,
          highestScore: curr.score ?? 0,
        };
      }

      acc[group].results.push({
        score: curr.score ?? 0,
        uuid: curr.uuid,
        group: curr.group,
        name: curr.name,
        description: curr.description,
        data: curr,
        referenceId: curr.referenceId,
        status: curr.status,
      });
      return acc;
    }, {} as Record<string, ISearchResultGroup>);

    const searchResultsGrouped = Object.values(searchResultsGroupedRecord)
      .map((curr) => {
        curr.results.sort((a, b) => {
          if (a.score === b.score) return 0;
          return (a.score ?? 0) > (b.score ?? 0) ? -1 : 1;
        });
        curr.averageScore = curr.results.reduce(
          (acc, curr) => acc + (curr.score ?? 0),
          0
        );
        curr.highestScore = curr.results[0].score;
        return curr;
      })
      .sort((a, b) => (b.results[0].score ?? 0) - (a.results[0].score ?? 0));

    const { pageSize } = result.data?.searchV3.pageInfo ?? { pageSize: 0 };
    const totalResultsLoaded = searchResults.length;
    const remainingResults = result.data?.searchV3
      ? result.data.searchV3.pageInfo.totalResults -
        result.data.searchV3.pageInfo.pageSize
      : 0;
    const remainingResultsText = pluralize('result', remainingResults);

    if (result.data?.searchV3.pageInfo.totalResults === 0) {
      convergeLogger.warn(
        `No results found for search query "${result.variables?.filters?.query}"`,
        result.variables
      );
    }

    return {
      searchResults,
      searchResultsGrouped,
      remainingResults,
      remainingResultsText,
      totalResultsLoaded,
      pageSize,
    };
  }, [result.data?.searchV3, result.variables]);

  return {
    search,
    result,
    searchResults,
    searchResultsGrouped,
    remainingResults,
    remainingResultsText,
    totalResultsLoaded,
    pageSize,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Name helpers
// ─────────────────────────────────────────────────────────────────────────────

const getSearchSubmissionName = (
  node: GetContextSearchQuery['searchV3']['edges'][0]['node']
): string => {
  if (node.data?.__typename !== 'SearchSubmission') return node.uuid ?? '';

  const { matchedOn, matchedType } = node.data;
  const allAliases = node.data.entities?.[0]?.aliases ?? [];
  const isNameSearch = node.detectedInputType === 'name';

  // Name search: matched alias leads. UUID/ref: fall back to primary alias.
  const leadName = isNameSearch && matchedOn ? matchedOn : null;
  const primaryAlias =
    allAliases.find(
      (a) =>
        a.type === AliasType.LegalName && a.source?.toLowerCase() === 'primary'
    ) ??
    allAliases.find((a) => a.type === AliasType.LegalName) ??
    allAliases[0];

  const displayName =
    leadName ?? primaryAlias?.name ?? node.data.referenceId ?? 'Submission';

  // Counterpart: LEGAL_NAME match → show DBA in parens, and vice versa.
  const displayType =
    isNameSearch && matchedType ? matchedType : primaryAlias?.type ?? null;
  const counterpartType =
    displayType === 'LEGAL_NAME' ? AliasType.TradingName : AliasType.LegalName;
  const counterpart = allAliases.find(
    (a) => a.type === counterpartType && a.name && a.name !== displayName
  );

  if (!counterpart?.name) return displayName;
  const counterpartLabel =
    counterpartType === AliasType.LegalName ? 'Legal Name' : 'DBA';
  return `${displayName} (${counterpartLabel}: ${counterpart.name})`;
};

const getSearchClaimName = (
  node: GetContextSearchQuery['searchV3']['edges'][0]['node']
): string => {
  if (node.data?.__typename !== 'SearchClaim') return node.uuid ?? '';

  const aliases = node.data.entity?.aliases ?? [];
  const primaryAlias =
    aliases.find((a) => a.type === AliasType.LegalName) ?? aliases[0];

  return primaryAlias?.name ?? node.data.referenceId ?? 'Claim';
};

const getSearchEntityName = (
  node: GetContextSearchQuery['searchV3']['edges'][0]['node']
): string => {
  if (node.data?.__typename !== 'SearchEntity') return node.uuid ?? '';

  const aliases = 'aliases' in node.data ? node.data.aliases ?? [] : [];
  const primaryAlias =
    aliases.find((a) => a.type === AliasType.LegalName) ?? aliases[0];
  if (primaryAlias?.name) return primaryAlias.name;

  const referenceId = 'referenceId' in node.data ? node.data.referenceId : null;
  return referenceId ?? node.uuid ?? 'Insured Entity';
};

const getSearchPersonName = (
  node: GetContextSearchQuery['searchV3']['edges'][0]['node']
): string => {
  if (node.data?.__typename !== 'SearchPerson') return node.uuid ?? '';
  const firstName = 'firstName' in node.data ? node.data.firstName : null;
  const lastName = 'lastName' in node.data ? node.data.lastName : null;
  if (firstName && lastName) return `${firstName} ${lastName}`;
  return lastName ?? firstName ?? node.uuid ?? 'Broker';
};
